import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { Day, DayAttribute, Event, GridCalendarDataProvider, GridCalendarLegendDataProvider, Month } from '../../model/grid-calendar';
import { hasTargetThatCanBeDisabled } from '../../utils/event-typeguards';
import { GridCalendarUtils } from '../../utils/grid-calendar-utils';

@Component( {
    selector: 'his-display-grid-calendar',
    templateUrl: './his-display-grid-calendar.component.html',
    styleUrls: ['./his-display-grid-calendar.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisDisplayGridCalendarComponent implements OnInit {
    private readonly gridCalendarUtil = inject( GridCalendarUtils );

    @Input() public firstMonth: string = '';
    @Input() public lastMonth: string = '';
    @Input() public showButtonRemoveFirstMonth: boolean = false;
    @Input() public showButtonRemoveLastMonth: boolean = false;
    @Input() public showButtonAddPreviousMonth: boolean = false;
    @Input() public showButtonAddFollowingMonth: boolean = false;
    @Input() public showButtonScrollBackward: boolean = false;
    @Input() public showButtonScrollForward: boolean = false;
    @Input() public showYearPicker: boolean = false;
    @Input() public showLegend: boolean = false;
    @Input( { required: true } ) public gridCalendarDataProvider!: GridCalendarDataProvider;
    @Input( { required: true } ) public gridCalendarLegendDataProvider!: GridCalendarLegendDataProvider;
    @Output() public startDateSelected: EventEmitter<Date | null> = new EventEmitter<Date | null>();
    @Output() public endDateSelected: EventEmitter<Date | null> = new EventEmitter<Date | null>();

    protected readonly minDate: Date = new Date( 1900, 0, 1, 0, 0, 0, 0 );
    protected readonly maxDate: Date = new Date( 2100, 11, 31, 0, 0, 0, 0 );
    public months: Month[] = [];
    public startDate: Date | null = null;
    public endDate: Date | null = null;
    public selectedYear: Date | null = null;
    public minimumYear: number = 1900;
    public maximumYear: number = 2100;

    ngOnInit(): void {
        let firstMonth = this.gridCalendarUtil.createMonthByString( this.firstMonth );
        let lastMonth = this.gridCalendarUtil.createMonthByString( this.lastMonth );
        this.loadCalendar( firstMonth, lastMonth );
    }

    private loadCalendar( firstMonth: Month | null, lastMonth: Month | null ): void {
        this.gridCalendarDataProvider.getGridCalendarData( firstMonth, lastMonth ).subscribe( gridCalendar => {
            if ( gridCalendar.months !== undefined ) {
                this.months = gridCalendar.months;
                this.markSelectedRange( this.startDate, this.endDate );
            }
            if ( gridCalendar.minimumTimestamp ) {
                this.minimumYear = new Date( gridCalendar.minimumTimestamp ).getFullYear();
            }
            if ( gridCalendar.maximumTimestamp ) {
                this.maximumYear = new Date( gridCalendar.maximumTimestamp ).getFullYear();
            }
        } );
    }

    loadYear( selectedYear: Date ): void {
        if ( selectedYear ) {
            let firstMonth = this.gridCalendarUtil.createMonthByDate( selectedYear );
            selectedYear.setMonth( selectedYear.getMonth() + 11 );
            let lastMonth = this.gridCalendarUtil.createMonthByDate( selectedYear );
            this.loadCalendar( firstMonth, lastMonth );
        }
    }

    calendarContainsDate( date: Date ): boolean {
        const isYearMonth = ( month: Month ) => month.year === date.getFullYear() && month.monthId === date.getMonth() + 1;
        const isDay = ( day: Day ) => day.dayId === date.getDate();
        const result = this.months.filter( isYearMonth ).map( month => month.days.find( isDay ) );
        return result.length === 1;
    }

    selectDate( selected: { day: Day, month: Month, isFrom: boolean } ): void {
        let date = new Date( selected.month.year, selected.month.monthId - 1, selected.day.dayId );
        if ( selected.isFrom ) {
            this.setStartDate( date );
        } else {
            this.setEndDate( date );
        }
    }

    setStartDate( date: Date ): void {
        let tempEndDate: Date | null = this.endDate;
        this.resetSelectedDays();
        if ( tempEndDate && tempEndDate.getTime() > date.getTime() ) {
            this.endDate = tempEndDate;
        } else {
            this.endDate = date;
        }
        this.startDate = date;
        this.markSelectedRange( this.startDate, this.endDate );
    }

    setEndDate( date: Date ): void {
        if ( this.startDate && this.startDate.getTime() > date.getTime() ) {
            return;
        }
        let tempStartDate: Date | null = this.startDate;
        this.resetSelectedDays();
        this.startDate = tempStartDate;
        this.endDate = date;
        this.markSelectedRange( this.startDate, this.endDate );
    }

    appendAttribute( date: Date, attribute: DayAttribute ): void {
        this.append( date, null, attribute );
    }

    appendEvent( date: Date, event: Event ): void {
        this.append( date, event, null );
    }

    resetSelectedDays(): void {
        this.startDate = null;
        this.endDate = null;
        this.startDateSelected.emit( null );
        this.endDateSelected.emit( null );
        this.months.forEach( month => month.days.forEach( day => day.isSelected = false ) );
    }

    private markSelectedRange( from: Date | null, to: Date | null ): void {
        this.resetSelectedDays();
        let dates: Date[] = [];
        if ( from && to ) {
            dates = this.gridCalendarUtil.getDatesBetween( from, to );
        } else if ( !from && to ) {
            dates.push( to );
        } else if ( from && !to ) {
            dates.push( from );
        } else {
            return;
        }
        dates.forEach( date => this.markDay( date ) );
        if ( dates.length > 0 ) {
            this.startDate = from;
            this.endDate = to;
            this.startDateSelected.emit( from );
            this.endDateSelected.emit( to );
        }
    }

    private markDay( date: Date ): void {
        const isDay = ( day: Day ) => day.dayId === date.getDate();
        const isYearMonth = ( month: Month ) => month.year === date.getFullYear() && month.monthId === date.getMonth() + 1;
        this.months.filter( isYearMonth ).map( month => {
            let day = month.days.find( isDay );
            if ( day ) {
                day.isSelected = true;
            }
        } );
    }

    private append( date: Date, event: Event | null, attribute: DayAttribute | null ): void {
        const isYearMonth = ( month: Month ) => month.year === date.getFullYear() && month.monthId === date.getMonth() + 1;
        const isDay = ( day: Day ) => day.dayId === date.getDate();
        if ( event ) {
            this.months.filter( isYearMonth ).map( month => month.days.find( isDay )?.events.push( event ) );
        } else if ( attribute ) {
            this.months.filter( isYearMonth ).map( month => month.days.find( isDay )?.dayAttributes.push( attribute ) );
        } else {
            console.error( 'Weder ein Event noch ein Attribut wurde angegeben!' );
        }
    }

    removeFirstMonth(): void {
        this.months.shift();
    }

    removeLastMonth(): void {
        this.months.pop();
    }

    addPreviousMonth( event: UIEvent ): void {
        this.addMonth( event, true );
    }

    addFollowingMonth( event: UIEvent ): void {
        this.addMonth( event, false );
    }

    scrollBackward( event: UIEvent ): void {
        this.addPreviousMonth( event );
        this.removeLastMonth();
    }

    scrollForward( event: UIEvent ): void {
        this.addFollowingMonth( event );
        this.removeFirstMonth();
    }

    private addMonth( event: UIEvent, onTop: boolean ): void {
        if ( !hasTargetThatCanBeDisabled( event ) ) {
            return;
        }

        event.target.disabled = true;

        let basedMonth: Month | undefined | null = this.getBasedMonth( onTop );
        let requestedMonth: Month = this.getRequestedMonth( basedMonth, onTop );
        this.gridCalendarDataProvider.getGridCalendarData( requestedMonth, null ).subscribe( {
            next: ( gridCalendar ) => {
                if ( gridCalendar.months && gridCalendar.months.length > 0 ) {
                    let loadedMonth = gridCalendar.months.at( 0 );
                    if ( loadedMonth ) {
                        if ( onTop ) {
                            this.months.unshift( loadedMonth );
                        } else {
                            this.months.push( loadedMonth );
                        }
                        this.markSelectedRange( this.startDate, this.endDate );
                    }
                }
                event.target.disabled = false;
            },
            error: () => {
                event.target.disabled = false;
            }
        } );
    }

    private getBasedMonth( onTop: boolean ): Month | undefined | null {
        if ( this.months && this.months.length > 0 ) {
            if ( onTop ) {
                return this.months.at( 0 );
            } else {
                return this.months.at( -1 );
            }
        }
        return null;
    }

    private getRequestedMonth( basedMonth: Month | undefined | null, onTop: boolean ): Month {
        if ( !basedMonth ) {
            return this.gridCalendarUtil.getCurrentMonth();
        } else if ( onTop ) {
            return this.gridCalendarUtil.decrementMonth( basedMonth );
        } else {
            return this.gridCalendarUtil.incrementMonth( basedMonth );
        }
    }

    reloadCalendar(): void {
        this.loadCalendar( this.months.at( 0 ), this.months.at( -1 ) );
    }
}
